import React, { useRef, useState } from 'react';
import UserAvatar from '../common/UserAvatar';
import { useProfile } from '../../hooks/useProfile';
import { useToast } from '../../hooks/useToast';

interface AvatarUploadProps {
  avatarUrl: string | null;
  firstName: string;
  lastName: string;
}

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

const AvatarUpload: React.FC<AvatarUploadProps> = ({ avatarUrl, firstName, lastName }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadAvatar, isUploading } = useProfile();
  const { showError } = useToast();
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      showError(
        { message: 'Solo se aceptan archivos JPEG, PNG y WebP' },
        'Formato no válido'
      );
      resetInput();
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      showError(
        { message: 'El archivo no debe exceder 5MB' },
        'Archivo muy grande'
      );
      resetInput();
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    try {
      await uploadAvatar(selectedFile);
      setPreview(null);
      setSelectedFile(null);
      resetInput();
    } catch {
      // Error handled by useProfile hook via toast
    }
  };

  const handleCancel = () => {
    setPreview(null);
    setSelectedFile(null);
    resetInput();
  };

  const resetInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading}
        className="relative group cursor-pointer disabled:cursor-not-allowed"
        aria-label="Cambiar foto de perfil"
      >
        {preview ? (
          <img
            src={preview}
            alt="Preview"
            className="w-32 h-32 rounded-full object-cover"
          />
        ) : (
          <UserAvatar avatarUrl={avatarUrl} firstName={firstName} lastName={lastName} size="lg" />
        )}
        <div className="absolute inset-0 rounded-full bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center transition-all">
          <span className="text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity">
            Cambiar
          </span>
        </div>
        {isUploading && (
          <div className="absolute inset-0 rounded-full bg-black bg-opacity-40 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        )}
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
        aria-label="Seleccionar foto de perfil"
      />

      {selectedFile && !isUploading && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleUpload}
            className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Confirmar
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Cancelar
          </button>
        </div>
      )}

      {!selectedFile && !isUploading && (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="text-sm text-blue-600 hover:text-blue-700"
        >
          Cambiar foto
        </button>
      )}
    </div>
  );
};

export default AvatarUpload;
